import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ProgressBar, familyOf } from '@/components/canvas/bits';

export function jumpTo(key) {
  const el = document.getElementById(`fld-${key}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const target = el.matches('input,textarea,button') ? el : el.querySelector('input,textarea,button');
  setTimeout(() => target?.focus({ preventScroll: true }), 350);
}

// The bar pinned to the bottom of a canvas: how much is filled in, what is missing, and the Confirm button.
export default function ConfirmBar({ def, state, label, confirming, missingOpen, onToggleMissing, onShowMissing, onConfirm, onReopen }) {
  const fam = familyOf(def);
  const missing = state.missing || [];
  const blockers = state.d.blockers || [];
  return (
    <div className="sticky bottom-0 z-(--z-sticky) -mx-4 border-t border-border bg-background/95 px-4 py-3 shadow-lg backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-2">
        {missingOpen && missing.length > 0 && !state.confirmed && (
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border bg-card p-2 text-sm">
            {missing.map((m) => (
              <li key={m.key}>
                <button
                  type="button"
                  onClick={() => {
                    onShowMissing();
                    jumpTo(m.key);
                  }}
                  className="flex w-full items-start gap-2 rounded-lg px-2 py-1 text-left hover:bg-accent"
                >
                  <ApperIcon name="CircleAlert" size={14} className="mt-1 shrink-0 text-destructive" />
                  <span>{m.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-44 flex-1 space-y-1">
            <div className="flex items-center justify-between gap-3 text-xs">
              <span className="font-medium tabular-nums">{state.completion}% of required answers</span>
              {!state.confirmed && missing.length > 0 && (
                <button type="button" className="font-medium text-primary hover:underline" onClick={onToggleMissing}>
                  {missing.length} still needed
                </button>
              )}
              {!state.confirmed && missing.length === 0 && blockers.length === 0 && <span className="font-medium text-success">Ready to confirm</span>}
              {!state.confirmed && missing.length === 0 && blockers.length > 0 && <span className="font-medium text-destructive">One thing to fix</span>}
            </div>
            <ProgressBar value={state.completion} barClassName={state.completion === 100 ? 'bg-success' : fam.solid} label="Required answers" />
          </div>
          {state.confirmed ? (
            <Button variant="outline" onClick={onReopen}>
              <ApperIcon name="LockOpen" size={16} /> Reopen to edit
            </Button>
          ) : (
            <Button size="lg" variant={state.canConfirm ? 'default' : 'secondary'} onClick={onConfirm} disabled={confirming}>
              {confirming ? (
                <>
                  <ApperIcon name="LoaderCircle" size={16} className="animate-spin" /> Confirming
                </>
              ) : (
                <>
                  <ApperIcon name="CircleCheck" size={18} /> Confirm {label}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
